import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import MapViewer from '../components/MapViewer';
import TreeBrowser from '../components/TreeBrowser';
import ActiveLayersPanel from '../components/ActiveLayersPanel';
import InspectionPanel from '../components/InspectionPanel';
import SettingsPanel from '../components/SettingsPanel';
import LoginButton from '../components/LoginButton';
import UserProfile from '../components/UserProfile';
import DemoBanner from '../components/DemoBanner';
import MapSharingDialog from '../components/MapSharingDialog';
import { useAuth } from '../hooks/useAuth';
import { useDemoMode } from '../hooks/useDemoMode';

const MapPage = () => {
  const { isDemoMode } = useDemoMode();
  const { isAuthenticated, loading, makeAuthenticatedRequest } = useAuth();
  
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [map, setMap] = useState(null);
  const [activeLayers, setActiveLayers] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [mobilePane, setMobilePane] = useState('map'); // 'browse' | 'edit' | 'map'
  const [showShareDialog, setShowShareDialog] = useState(false);
  // Removed post-add coach mark; keep UI simple after auto-switch
  const [showOnboardingHint, setShowOnboardingHint] = useState(false);
  const addTabRef = useRef(null);
  const coachRef = useRef(null);
  const [coachPos, setCoachPos] = useState({ left: 12, top: 0, arrowLeft: 24 });
  const coachShownRef = useRef(false);

  // Update page title based on demo mode
  useEffect(() => {
    document.title = isDemoMode ? 'ShareShape Demo' : 'ShareShape';
  }, [isDemoMode]);

  // Dynamic positioning for left navigation based on header heights
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const updateNavigationPosition = () => {
      const demoBanner = document.querySelector('.demo-banner');
      const appHeader = document.querySelector('.app-header');
      const leftNavigation = document.querySelector('.left-navigation');
      
      if (leftNavigation) {
        let totalHeight = 0;
        
        if (demoBanner) {
          totalHeight += demoBanner.offsetHeight;
        }
        
        if (appHeader) {
          totalHeight += appHeader.offsetHeight;
        }
        
        leftNavigation.style.marginTop = `${totalHeight}px`;
      }
    };

    // Update position immediately
    updateNavigationPosition();
    
    // Update position when window resizes or content changes
    window.addEventListener('resize', updateNavigationPosition);
    
    // Use a small delay to ensure elements are rendered
    const timeoutId = setTimeout(updateNavigationPosition, 100);
    
    return () => {
      window.removeEventListener('resize', updateNavigationPosition);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [isDemoMode, isAuthenticated]); // Re-run when demo mode or auth status changes

  // In demo mode, users will sync files from the actual SharePoint demo site
  // No preloaded layers - they'll see the real SharePoint files and sync them
  const [leftPanelTab, setLeftPanelTab] = useState('files'); // 'files', 'sharing', 'settings', or null
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const [isInspectionMode, setIsInspectionMode] = useState(false);
  
  // SharePoint state persistence
  const [sharepointState, setSharepointState] = useState({
    sites: [],
    selectedSite: null,
    drives: [],
    selectedDrive: null,
    treeData: [],
    expandedFolders: new Set(),
    datasets: [],
    isInitialized: false
  });

  const handleAddLayer = (dataset) => {
    // Check if layer is already active
    if (!activeLayers.find(layer => layer.id === dataset.id)) {
      setActiveLayers(prev => [...prev, dataset]);
      // On mobile, switch to the map pane so users see the result
      if (isMobile && mobilePane !== 'map') {
        setMobilePane('map');
      }
      // Hide the onboarding hint once a layer is added
      if (showOnboardingHint) {
        setShowOnboardingHint(false);
      }
    }
  };

  const handleRemoveLayer = (datasetId) => {
    setActiveLayers(prev => prev.filter(layer => layer.id !== datasetId));
  };

  const handleClearAllLayers = () => {
    setActiveLayers([]);
  };

  const handleReorderLayers = (layerId, insertIndex) => {
    setActiveLayers(prev => {
      const currentIndex = prev.findIndex(layer => layer.id === layerId);
      if (currentIndex === -1) return prev;
      
      const reorderedLayers = [...prev];
      const [movedLayer] = reorderedLayers.splice(currentIndex, 1);
      reorderedLayers.splice(insertIndex, 0, movedLayer);
      
      return reorderedLayers;
    });
  };

  const handleNavIconClick = (tabName) => {
    if (leftPanelTab === tabName && isLeftPanelOpen) {
      // Clicking the same icon when panel is open - close the panel
      setIsLeftPanelOpen(false);
    } else {
      // Opening a different panel or reopening a closed panel
      setLeftPanelTab(tabName);
      setIsLeftPanelOpen(true);
    }
  };

  const handleClosePanel = () => {
    setIsLeftPanelOpen(false);
  };

  const handleMapReady = (mapInstance) => {
    setMap(mapInstance);
    // Make mapbox available globally for the LayerControl
    window.mapboxgl = require('mapbox-gl');
  };

  // Ensure Mapbox resizes correctly when showing the map pane on mobile
  useEffect(() => {
    if (!map) return;
    // Resize when switching to the map pane
    if (isMobile && mobilePane === 'map') {
      // Allow layout to settle before resize
      setTimeout(() => {
        try { map.resize && map.resize(); } catch (_) {}
      }, 0);
    }
  }, [isMobile, mobilePane, map]);

  // Resize map on orientation change / viewport changes on mobile
  useEffect(() => {
    if (!map) return;
    const handleWindowResize = () => {
      try { map.resize && map.resize(); } catch (_) {}
    };
    window.addEventListener('orientationchange', handleWindowResize);
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('orientationchange', handleWindowResize);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [map]);

  // No post-add coach mark; nothing to clean up

  // Show onboarding coach mark once per app load on mobile
  useEffect(() => {
    if (isMobile && !coachShownRef.current) {
      coachShownRef.current = true;
      // Defer to ensure tabs are rendered
      setTimeout(() => setShowOnboardingHint(true), 0);
    }
  }, [isMobile]);

  // Close the coach mark when any button is clicked
  useEffect(() => {
    const closeOnAnyButton = (e) => {
      if (showOnboardingHint) {
        const el = e.target.closest && e.target.closest('button');
        if (el) setShowOnboardingHint(false);
      }
    };
    window.addEventListener('click', closeOnAnyButton, true);
    return () => window.removeEventListener('click', closeOnAnyButton, true);
  }, [showOnboardingHint]);

  // Position onboarding coach mark relative to the Add Data tab
  useEffect(() => {
    if (!showOnboardingHint) return;
    const updateCoachPosition = () => {
      const btn = addTabRef.current;
      const coach = coachRef.current;
      if (!btn || !coach) return;
      const btnRect = btn.getBoundingClientRect();
      const coachRect = coach.getBoundingClientRect();
      const gap = 10;
      let left = btnRect.left + btnRect.width / 2 - coachRect.width / 2;
      left = Math.max(8, Math.min(window.innerWidth - coachRect.width - 8, left));
      let top = btnRect.top - coachRect.height - gap;
      top = Math.max(8, top);
      let arrowLeft = btnRect.left + btnRect.width / 2 - left - 8; // 8 ~ arrow half width
      arrowLeft = Math.max(8, Math.min(coachRect.width - 16, arrowLeft));
      setCoachPos({ left, top, arrowLeft });
    };
    const id = setTimeout(updateCoachPosition, 0);
    window.addEventListener('resize', updateCoachPosition);
    window.addEventListener('orientationchange', updateCoachPosition);
    return () => {
      clearTimeout(id);
      window.removeEventListener('resize', updateCoachPosition);
      window.removeEventListener('orientationchange', updateCoachPosition);
    };
  }, [showOnboardingHint]);

  const handleSyncComplete = async (syncData) => {
    // Automatically add the synced dataset to the map
    if (syncData && syncData.datasetId) {
      // Check if the dataset is already in activeLayers (to prevent duplicates)
      const existingLayer = activeLayers.find(layer => layer.id === syncData.datasetId);
      if (existingLayer) {
        return;
      }
      
      // Skip handleSyncComplete for photo collections since they're already added by syncPhotoCollection
      if (syncData.geometryType?.toLowerCase() === 'photocollection') {
        return;
      }
      
      try {
        // Fetch complete dataset information from the backend
        const metadataUrl = isDemoMode 
          ? `/api/demo/data/datasets/${syncData.datasetId}/metadata`
          : `/api/data/datasets/${syncData.datasetId}/metadata`;
        const response = await makeAuthenticatedRequest(metadataUrl);
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            handleAddLayer(result.data);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching complete dataset info:', error);
      }
      
      // Only use fallback if we have valid geometry type from sync response
      if (syncData.geometryType && syncData.geometryType !== 'Unknown') {
        const dataset = {
          id: syncData.datasetId,
          name: syncData.fileName || 'Unknown',
          recordCount: syncData.recordCount || 0,
          geometryType: syncData.geometryType,
          geometry_type: syncData.geometryType  // Add both formats for compatibility
        };
        
        handleAddLayer(dataset);
      } else {
        console.warn('Skipping layer addition - no valid geometry type available');
      }
    }
  };

  const handleToggleInspectionMode = () => {
    setIsInspectionMode(prev => !prev);
  };


  const handleDataDeleted = () => {
    // Remove all layers from the map first
    if (map) {
      activeLayers.forEach(layer => {
        const layerId = `layer-${layer.id}`;
        const sourceId = `source-${layer.id}`;
        const fillLayerId = `${layerId}-fill`;
        const strokeLayerId = `${layerId}-stroke`;
        
        // Remove layers
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
        if (map.getLayer(strokeLayerId)) map.removeLayer(strokeLayerId);
        
        // Remove sources
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
    }
    
    // Clear active layers state
    setActiveLayers([]);
    
    // Reset SharePoint state to force refresh
    setSharepointState(prev => ({
      ...prev,
      datasets: [],
      treeData: [],
      expandedFolders: new Set()
    }));
  };

  if (loading) {
    return (
      <div className="map-page">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`map-page ${isDemoMode ? 'demo-mode' : ''}`}>
      {isDemoMode && <DemoBanner />}
      <header className="app-header">
        <Link to="/" className="header-logo">
          <svg className="header-icon" viewBox="0 0 24 24">
            <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM10 5.47l4 1.4v11.66l-4-1.4V5.47zm-5 .99l3-1.01v11.7l-3 1.16V6.46zm14 11.08l-3 1.01V6.86l3-1.16v11.84z"/>
          </svg>
          <span>ShareShape Map Creator</span>
          <span className="beta-badge">BETA</span>
        </Link>
        <div className="header-info">
          <UserProfile />
        </div>
      </header>

      {/* Mobile layout: three collapsible panes */}
      {isMobile ? (
        <div className="mobile-content">
          <div className="mobile-pane-container">
            {/* Keep map mounted across pane switches to preserve state */}
            <div 
              className="mobile-pane map-pane" 
              style={{ visibility: mobilePane === 'map' ? 'visible' : 'hidden', pointerEvents: mobilePane === 'map' ? 'auto' : 'none' }}
            >
              <main className="map-area">
                <MapViewer 
                  selectedDataset={selectedDataset}
                  onMapReady={handleMapReady}
                />
              </main>
            </div>

            {mobilePane === 'browse' && (
              <div className="mobile-pane browse-pane">
                <div className="sidebar-header">
                  <h3>Browse / Add Data</h3>
                </div>
                <div className="sidebar-content">
                  <TreeBrowser 
                    onAddLayer={handleAddLayer}
                    activeLayers={activeLayers}
                    onSyncComplete={handleSyncComplete}
                    sharepointState={sharepointState}
                    setSharepointState={setSharepointState}
                  />
                </div>
              </div>
            )}

            {mobilePane === 'edit' && (
              <div className="mobile-pane edit-pane">
                <div className="sidebar-header">
                  <h3>Edit / Manage Data</h3>
                </div>
                <div className="sidebar-content">
                  <ActiveLayersPanel 
                    map={map}
                    activeLayers={activeLayers}
                    onRemoveLayer={handleRemoveLayer}
                    onClearAllLayers={handleClearAllLayers}
                    onReorderLayers={handleReorderLayers}
                    isLeftPanelOpen={false}
                    hideShareControls={true}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Headless layer sync on mobile when edit pane isn't open */}
          {mobilePane !== 'edit' && (
            <div style={{ display: 'none' }} aria-hidden>
              <ActiveLayersPanel 
                map={map}
                activeLayers={activeLayers}
                onRemoveLayer={handleRemoveLayer}
                onClearAllLayers={handleClearAllLayers}
                onReorderLayers={handleReorderLayers}
                isLeftPanelOpen={false}
                hideShareControls={true}
              />
            </div>
          )}

          {/* Share button: only on Map tab (more prominent) */}
          {mobilePane === 'map' && map && activeLayers.length > 0 && (
            <button 
              className="mobile-share-button"
              onClick={() => setShowShareDialog(true)}
              title="Share this map"
            >
              🔗 Share Map
            </button>
          )}

          {/* Onboarding coach mark pointing to Add Data tab */}
          {showOnboardingHint && (
            <div
              ref={coachRef}
              className="mobile-coach"
              role="note"
              aria-live="polite"
              style={{ left: `${coachPos.left}px`, top: `${coachPos.top}px`, bottom: 'auto' }}
            >
              <div className="coach-content">
                <strong>Add data to get started</strong>
                <div className="coach-sub">Tap “Add Data” to browse your files</div>
              </div>
              <button
                className="coach-dismiss"
                aria-label="Dismiss hint"
                onClick={() => {
                  setShowOnboardingHint(false);
                }}
              >✕</button>
              <div className="coach-arrow" style={{ left: `${coachPos.arrowLeft}px` }} />
            </div>
          )}

          {/* Post-add coach mark removed */}

          {/* Mobile tab bar */}
          <div className="mobile-tabbar">
            <button 
              className={`mobile-tab ${mobilePane === 'browse' ? 'active' : ''}`}
              onClick={() => setMobilePane('browse')}
              title="Add Data"
              ref={addTabRef}
            >
              ➕ Add Data
            </button>
            <button 
              className={`mobile-tab ${mobilePane === 'edit' ? 'active' : ''}`}
              onClick={() => setMobilePane('edit')}
              title="Edit / Manage Data"
            >
              ✏️ Edit
            </button>
            <button 
              className={`mobile-tab ${mobilePane === 'map' ? 'active' : ''}`}
              onClick={() => setMobilePane('map')}
              title="View Map"
            >
              🗺️ Map
            </button>
          </div>

          {/* Share dialog (mobile, no left overlay offset) */}
          <MapSharingDialog
            isOpen={showShareDialog}
            onClose={() => setShowShareDialog(false)}
            map={map}
            activeLayers={activeLayers}
            layerGroups={[]}
            isLeftPanelOpen={false}
            leftOverlayOffset={0}
          />
        </div>
      ) : (
        <div className="app-content">
        <div className="left-navigation">
          <div className="nav-rail">
            <button
              className={`nav-icon ${leftPanelTab === 'files' && isLeftPanelOpen ? 'active' : ''}`}
              onClick={() => handleNavIconClick('files')}
              title="Data Sources"
            >
              📁
            </button>
            <button
              className={`nav-icon ${leftPanelTab === 'inspection' && isLeftPanelOpen ? 'active' : ''}`}
              onClick={() => handleNavIconClick('inspection')}
              title="Feature Inspection"
            >
              🔍
            </button>
            <button
              className={`nav-icon ${leftPanelTab === 'settings' && isLeftPanelOpen ? 'active' : ''}`}
              onClick={() => handleNavIconClick('settings')}
              title="Settings"
            >
              ⚙️
            </button>
          </div>

          {isLeftPanelOpen && (
            <aside className="sidebar">
              <div className="sidebar-header">
                <h3>
                  {leftPanelTab === 'files' ? 'Data Sources' : 
                   leftPanelTab === 'inspection' ? '🔍 Feature Inspection' : '⚙️ Settings'}
                </h3>
                <button 
                  className="close-btn"
                  onClick={handleClosePanel}
                  title="Close panel"
                >
                  ✕
                </button>
              </div>
              <div className="sidebar-content">
                {leftPanelTab === 'files' && (
                  <TreeBrowser 
                    onAddLayer={handleAddLayer}
                    activeLayers={activeLayers}
                    onSyncComplete={handleSyncComplete}
                    sharepointState={sharepointState}
                    setSharepointState={setSharepointState}
                  />
                )}
                {leftPanelTab === 'inspection' && (
                  <InspectionPanel 
                    map={map}
                    isInspectionMode={isInspectionMode}
                    onToggleInspectionMode={handleToggleInspectionMode}
                  />
                )}
                {leftPanelTab === 'settings' && (
                  <SettingsPanel onDataDeleted={handleDataDeleted} />
                )}
              </div>
            </aside>
          )}
        </div>

        <main className="map-area">
          <MapViewer 
            selectedDataset={selectedDataset}
            onMapReady={handleMapReady}
          />
        </main>

        <aside className="layers-panel">
          <div className="sidebar-header">
            <h3>🗺️ Active Layers</h3>
          </div>
          <div className="sidebar-content">
            <ActiveLayersPanel 
              map={map}
              activeLayers={activeLayers}
              onRemoveLayer={handleRemoveLayer}
              onClearAllLayers={handleClearAllLayers}
              onReorderLayers={handleReorderLayers}
              isLeftPanelOpen={isLeftPanelOpen}
            />
          </div>
        </aside>
      </div>
      )}

      {/* Login overlay when not authenticated and not loading and not in demo mode */}
      {!isAuthenticated && !loading && !isDemoMode && (
        <div className="login-overlay">
          <LoginButton />
        </div>
      )}
    </div>
  );
};

export default MapPage;
